import { Component, OnInit, Input, OnDestroy, signal } from '@angular/core';
import { BigFormService } from '@flexus/core';
import { Observable, Subscription } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';

interface CsvPaymentItem {
	job_id?: string | number;
	['SP Name']: string;
	['Tenant']: string;
	['SP Bank account name']: string;
	['Bank account type\nCheque = 1\nSaving = 2']: string;
	['Bank account branch']: string;
	['Bank account number']: string;
	['Amount']: number;
	['Beneficiary statement reference']: string;
	['SP POP email']: string;
	['SP SMS']: string;
}

interface CsvExcludeEvent {
	item: CsvPaymentItem;
	exclude: boolean;
}

@Component({
	selector: 'flx-pinggo-csv',
	templateUrl: 'pinggo-csv-payments.component.html',
	styleUrls: ['pinggo-csv-payments.component.scss']
})
export class PinggoCSVPaymentComponent implements OnInit, OnDestroy {
	private static readonly ALL_TENANTS = '__ALL_TENANTS__';

	@Input() csvPayments$!: Observable<CsvPaymentItem[]>;
	csv_payments$!: Observable<CsvPaymentItem[]>;
	paymentSub!: Subscription;
	paymentitems: CsvPaymentItem[] = [];
	displayed_payments_array: CsvPaymentItem[] = [];
	included_payments_array: CsvPaymentItem[] = [];
	excluded_payments_array: CsvPaymentItem[] = [];
	tenantOptions = signal<Array<{ display: string; value: string }>>([]);
	selectedTenant = signal<string>(PinggoCSVPaymentComponent.ALL_TENANTS);
	tenantDropdownDisplayOptions = { displayKey: 'display', valueKey: 'value' };
	private manuallyExcludedPaymentKeys = signal<Set<string>>(new Set<string>());

	constructor(public bf: BigFormService) {}

	ngOnInit(): void {
		this.paymentSub = this.csvPayments$
			.pipe(
				skipWhile((csvlist: CsvPaymentItem[]) => !csvlist || csvlist.length === 0),
				take(1),
				map((result: CsvPaymentItem[]) => {
					this.paymentitems = result;
					this.setTenantOptions();
					this.setDisplayedPaymentsForTenant();
					this.recomputePaymentSets();
				})
			)
			.subscribe();
	}

	handleExcludeItem(event: CsvExcludeEvent | null): void {
		if (!event) {
			this.recomputePaymentSets();
			return;
		}

		const { item, exclude } = event;
		const currentKeys = new Set<string>(this.manuallyExcludedPaymentKeys());
		const itemKey = this.getPaymentKey(item);

		if (exclude) {
			currentKeys.add(itemKey);
		} else {
			currentKeys.delete(itemKey);
		}

		this.manuallyExcludedPaymentKeys.set(currentKeys);
		this.recomputePaymentSets();
	}

	onTenantSelected(tenant: string): void {
		this.selectedTenant.set(tenant || PinggoCSVPaymentComponent.ALL_TENANTS);
		this.setDisplayedPaymentsForTenant();
		this.recomputePaymentSets();
	}

	private setTenantOptions(): void {
		const uniqueTenants = Array.from(
			new Set(
				this.paymentitems
					.map((item: CsvPaymentItem) => item['Tenant'])
					.filter((tenant: string | undefined) => !!tenant)
			)
		).sort((a: string, b: string) => a.localeCompare(b));

		this.tenantOptions.set([
			{ display: 'All tenants', value: PinggoCSVPaymentComponent.ALL_TENANTS },
			...uniqueTenants.map((tenant: string) => ({ display: tenant, value: tenant }))
		]);
	}

	private recomputePaymentSets(): void {
		const manuallyExcludedPaymentKeys = this.manuallyExcludedPaymentKeys();
		this.included_payments_array = this.displayed_payments_array.filter((item: CsvPaymentItem) => {
			return !manuallyExcludedPaymentKeys.has(this.getPaymentKey(item));
		});

		const includedPaymentKeys = new Set<string>(this.included_payments_array.map((item: CsvPaymentItem) => this.getPaymentKey(item)));
		this.excluded_payments_array = this.paymentitems.filter((item: CsvPaymentItem) => !includedPaymentKeys.has(this.getPaymentKey(item)));

		this.bf.patchValues({
			excluded_payments: this.excluded_payments_array,
			included_payments: this.included_payments_array
		});

		this.bf.getControl('all_excluded').setValue(this.included_payments_array.length > 0);
		this.bf.bigForm.updateValueAndValidity();
	}

	private setDisplayedPaymentsForTenant(): void {
		const selectedTenant = this.selectedTenant();
		this.displayed_payments_array = this.paymentitems.filter((item: CsvPaymentItem) => {
			return selectedTenant === PinggoCSVPaymentComponent.ALL_TENANTS || item['Tenant'] === selectedTenant;
		});
	}

	private getPaymentKey(item: CsvPaymentItem): string {
		if (item.job_id !== undefined && item.job_id !== null && item.job_id !== '') {
			return `job:${String(item.job_id)}`;
		}

		// Some rows can arrive without a job id; build a stable row key to avoid key-collisions.
		return [
			item['Tenant'] ?? '',
			item['SP Name'] ?? '',
			item['Bank account number'] ?? '',
			String(item['Amount'] ?? ''),
			item['Beneficiary statement reference'] ?? '',
			item['SP POP email'] ?? '',
			item['SP SMS'] ?? ''
		].join('|');
	}

	ngOnDestroy(): void {
		if (this.paymentSub) {
			this.paymentSub.unsubscribe();
		}
	}
}
